import { FunctionComponent, MouseEvent, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { copyText } from "@/utils/copyText";

interface ReplyReasoningPopoverProps {
	/** Reply Agent's reasoning for the draft (why this message / timing / channel was chosen). */
	reasoning?: string | null;
	onCopy?: (value: string) => void;
}

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

/**
 * Reply-draft reasoning hover (PREC-3617): the Reply Agent's "why this message / timing / channel"
 * justification for a generated reply draft, with click-to-copy. Mirrors the CategoryReasoningPopover
 * / LeadScoringPopover card shell and is Tailwind-only, so it is safe inside the extension shadow root.
 */
export const ReplyReasoningPopover: FunctionComponent<ReplyReasoningPopoverProps> = ({
	reasoning,
	onCopy,
}) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);
	const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;

			if (copiedTimerRef.current) {
				clearTimeout(copiedTimerRef.current);
			}
		};
	}, []);

	if (!reasoning) {
		return null;
	}

	const copy = (e: MouseEvent) => {
		stop(e);

		void copyText(reasoning).then((copied) => {
			if (!copied || !mountedRef.current) {
				return;
			}

			onCopy?.(reasoning);
			setCopied(true);

			if (copiedTimerRef.current) {
				clearTimeout(copiedTimerRef.current);
			}

			copiedTimerRef.current = setTimeout(() => setCopied(false), 1000);
		});
	};

	return (
		<div className="w-[300px]">
			<div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
				<Icon className="h-[12px] w-[12px]" icon={faSparkles} />
				{t("inbox.draft.reasoningHeading")}
			</div>
			<div className="my-1 h-px bg-gray-200" />
			<div
				className={`group/val max-h-[300px] cursor-pointer overflow-y-auto rounded-md px-2 py-1.5 transition-colors ${
					copied ? "bg-status-success-light" : "hover:bg-gray-50"
				}`}
				onClick={copy}
			>
				<div className="flex items-start gap-2">
					<span className="flex-1 whitespace-normal text-[11.5px] leading-relaxed text-text-meta">
						{reasoning}
					</span>
					<Icon
						className={`mt-0.5 h-[14px] w-[14px] shrink-0 transition-opacity ${
							copied
								? "text-status-success opacity-100"
								: "text-text-meta opacity-0 group-hover/val:opacity-100"
						}`}
						icon={copied ? faCheck : faCopy}
					/>
				</div>
			</div>
		</div>
	);
};
